import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useGetCourseQuery } from "@/entities/course";

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
  }
}

/**
 * Подключает счётчик Яндекс.Метрики, если у текущего курса задан
 * `yandex_metrika_id`, и трекает переходы между страницами SPA.
 *
 * - Скрипт счётчика вставляется один раз (защита от повторной инициализации
 *   при ре-рендерах / React.StrictMode).
 * - При смене роута вызывается `ym(id, 'hit', url)` — иначе в Метрике виден
 *   только первый вход на сайт, а уроки/экраны не считаются.
 * - Если ID не задан — не делаем ничего.
 */
export const useYandexMetrika = () => {
  const { data: course } = useGetCourseQuery();
  const location = useLocation();

  const counterId = course?.yandex_metrika_id?.trim();

  // ID счётчика, для которого уже инициализирован тег.
  const initializedId = useRef<string | null>(null);

  // Инициализация счётчика (один раз на ID).
  useEffect(() => {
    if (!counterId) return;
    if (initializedId.current === counterId) return;
    if (typeof window === "undefined") return;

    initializedId.current = counterId;

    const scriptSrc = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;

    // Стандартный загрузчик Яндекс.Метрики (с проверкой, что тег ещё не вставлен).
    (function (
      m: Window & { ym?: any },
      e: Document,
      t: string,
      r: string,
      i: "ym"
    ) {
      m[i] =
        m[i] ||
        function (...args: unknown[]) {
          (m[i].a = m[i].a || []).push(args);
        };
      m[i].l = 1 * Number(new Date());
      for (let j = 0; j < e.scripts.length; j++) {
        if (e.scripts[j].src === r) return;
      }
      const k = e.createElement(t) as HTMLScriptElement;
      const a = e.getElementsByTagName(t)[0];
      k.async = true;
      k.src = r;
      a.parentNode?.insertBefore(k, a);
    })(window, document, "script", scriptSrc, "ym");

    window.ym?.(Number(counterId), "init", {
      webvisor: true,
      clickmap: true,
      accurateTrackBounce: true,
      trackLinks: true,
      defer: true,
    });
  }, [counterId]);

  // SPA-трекинг: новый просмотр при каждой смене роута.
  useEffect(() => {
    if (!counterId) return;
    if (initializedId.current !== counterId) return;
    if (typeof window === "undefined" || !window.ym) return;

    window.ym(Number(counterId), "hit", window.location.href);
  }, [counterId, location.pathname]);
};
